import { Link, createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { DataGridPro, type GridColDef, type GridSortModel } from "@mui/x-data-grid-pro";
import { FlexGrid } from "@/grids/gridDefaults";
import { serverGridDefaults } from "@/grids/serverGridDefaults";
import { workPackageListQueryOptions } from "@/lib/queries";
import { NewManualContractDialog } from "@/components/contracts/NewManualContractDialog";
import type {
  ContractWorkPackageMode,
  ContractWorkPackageStatus,
  WorkPackageListItem,
} from "@/lib/types";

type QueueSearch = {
  q?: string;
  status?: ContractWorkPackageStatus;
  mode?: ContractWorkPackageMode;
  page: number;
  pageSize: number;
  sort: "updated_at" | "created_at" | "contract_no" | "project_code" | "status";
  order: "asc" | "desc";
  create?: boolean;
  /** Job to preselect when arriving from that job's screens. */
  project?: string;
};

const statuses: ContractWorkPackageStatus[] = [
  "preparing",
  "prepared",
  "review_requested",
  "reviewed",
  "executing",
  "failed",
  "completed",
];
const modes: ContractWorkPackageMode[] = ["agent", "hybrid", "manual", "import"];

export const Route = createFileRoute("/_auth/contract-work-packages/")({
  validateSearch: (raw: Record<string, unknown>): QueueSearch => ({
    q: typeof raw.q === "string" && raw.q ? raw.q : undefined,
    status: statuses.includes(raw.status as ContractWorkPackageStatus)
      ? (raw.status as ContractWorkPackageStatus)
      : undefined,
    mode: modes.includes(raw.mode as ContractWorkPackageMode)
      ? (raw.mode as ContractWorkPackageMode)
      : undefined,
    page: Math.max(0, Number(raw.page) || 0),
    pageSize: [25, 50, 100].includes(Number(raw.pageSize)) ? Number(raw.pageSize) : 50,
    sort: ["updated_at", "created_at", "contract_no", "project_code", "status"].includes(
      String(raw.sort),
    )
      ? (raw.sort as QueueSearch["sort"])
      : "updated_at",
    order: raw.order === "asc" ? "asc" : "desc",
    create: raw.create === true || raw.create === "true" ? true : undefined,
    project: typeof raw.project === "string" && raw.project ? raw.project : undefined,
  }),
  component: ContractReviewQueuePage,
});

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusColor(status: ContractWorkPackageStatus) {
  if (status === "completed") return "success" as const;
  if (status === "failed") return "error" as const;
  if (status === "executing" || status === "preparing") return "info" as const;
  if (status === "review_requested") return "warning" as const;
  return "default" as const;
}

function ContractReviewQueuePage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchText, setSearchText] = useState(search.q ?? "");

  useEffect(() => setSearchText(search.q ?? ""), [search.q]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const q = searchText.trim() || undefined;
      if (q === search.q) return;
      void navigate({ search: (previous) => ({ ...previous, q, page: 0 }), replace: true });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [navigate, search.q, searchText]);

  const query = useQuery(
    workPackageListQueryOptions({
      search: search.q,
      status: search.status,
      mode: search.mode,
      sort: search.sort,
      order: search.order,
      limit: search.pageSize,
      offset: search.page * search.pageSize,
    }),
  );
  const page = query.data
    && Array.isArray(query.data.items)
    && typeof query.data.total === "number"
    ? query.data
    : undefined;

  const columns = useMemo<GridColDef<WorkPackageListItem>[]>(
    () => [
      {
        field: "contract_no",
        headerName: "Contract",
        minWidth: 245,
        flex: 1.2,
        renderCell: ({ row }) => (
          <Stack sx={{ minWidth: 0, lineHeight: 1.25 }}>
            <Link
              to="/contract-work-packages/$packageId"
              params={{ packageId: row.id }}
              aria-label={`Open contract workspace ${row.contract_no}`}
              title={`Open ${row.contract_no}`}
              className="font-semibold text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-700 dark:text-blue-400 dark:decoration-blue-700 dark:hover:text-blue-300"
            >
              {row.contract_no}
            </Link>
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.title}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "project_code",
        headerName: "Project",
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) => (
          <Stack sx={{ minWidth: 0, lineHeight: 1.25 }}>
            <Typography variant="body2" fontWeight={600}>{row.project_code}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>{row.project_name}</Typography>
          </Stack>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 165,
        renderCell: ({ value }) => (
          <Chip size="small" label={humanize(String(value))} color={statusColor(value)} />
        ),
      },
      {
        field: "mode",
        headerName: "Prepared by",
        minWidth: 125,
        valueFormatter: (value) => humanize(String(value)),
      },
      {
        field: "diagnostic_summary",
        headerName: "Checks",
        sortable: false,
        minWidth: 180,
        renderCell: ({ row }) => {
          const diagnostic = row.diagnostic_summary;
          return diagnostic.blocking_count
            ? `${diagnostic.blocking_count} blocking`
            : diagnostic.warning_count
              ? `${diagnostic.warning_count} warning${diagnostic.warning_count === 1 ? "" : "s"}`
              : "Ready";
        },
      },
      {
        field: "updated_at",
        headerName: "Updated",
        type: "dateTime",
        minWidth: 190,
        valueGetter: (value) => (value ? new Date(String(value)) : null),
      },
    ],
    [],
  );

  const sortModel: GridSortModel = [{ field: search.sort, sort: search.order }];

  return (
    <Box sx={{ height: "calc(100vh - 56px)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Contract workspaces</Typography>
            <Typography color="text.secondary" variant="body2">
              Build manually or review agent-prepared contracts, evidence, exceptions, and execution status.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => void navigate({
            search: (previous) => ({ ...previous, create: true }),
          })}>New manual contract</Button>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField
            size="small"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search contract or project"
            aria-label="Search contract work packages"
            sx={{ minWidth: { md: 320 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> } }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={search.status ?? ""}
            onChange={(event) => void navigate({
              search: (previous) => ({
                ...previous,
                status: (event.target.value || undefined) as ContractWorkPackageStatus | undefined,
                page: 0,
              }),
            })}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {statuses.map((status) => <MenuItem key={status} value={status}>{humanize(status)}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Prepared by"
            value={search.mode ?? ""}
            onChange={(event) => void navigate({
              search: (previous) => ({
                ...previous,
                mode: (event.target.value || undefined) as ContractWorkPackageMode | undefined,
                page: 0,
              }),
            })}
            sx={{ minWidth: 165 }}
          >
            <MenuItem value="">All methods</MenuItem>
            {modes.map((mode) => <MenuItem key={mode} value={mode}>{humanize(mode)}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            {page
              ? `${page.total.toLocaleString()} ${page.total === 1 ? "package" : "packages"}`
              : "Loading…"}
          </Typography>
        </Stack>
      </Box>
      {query.isError && <Alert severity="error">Could not load the review queue.</Alert>}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <FlexGrid>
          <DataGridPro
            {...serverGridDefaults}
            rows={page?.items ?? []}
            columns={columns}
            rowCount={page?.total ?? 0}
            loading={query.isLoading || query.isFetching}
            paginationModel={{ page: search.page, pageSize: search.pageSize }}
            onPaginationModelChange={(model) => void navigate({
              search: (previous) => ({ ...previous, page: model.page, pageSize: model.pageSize }),
            })}
            pageSizeOptions={[25, 50, 100]}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              const next = model[0];
              if (!next) return;
              void navigate({
                search: (previous) => ({
                  ...previous,
                  sort: next.field as QueueSearch["sort"],
                  order: next.sort === "asc" ? "asc" : "desc",
                  page: 0,
                }),
              });
            }}
            disableRowSelectionOnClick
            onRowClick={({ row }) => void navigate({
              to: "/contract-work-packages/$packageId",
              params: { packageId: row.id },
            })}
            onCellKeyDown={({ row }, event) => {
              if (event.key !== "Enter") return;
              void navigate({
                to: "/contract-work-packages/$packageId",
                params: { packageId: row.id },
              });
            }}
            sx={{
              "& .MuiDataGrid-row": { cursor: "pointer" },
              "& .MuiDataGrid-row:hover": { bgcolor: "action.hover" },
            }}
            rowHeight={64}
          />
        </FlexGrid>
      </Box>
      <NewManualContractDialog
        open={Boolean(search.create)}
        projectId={search.project}
        onClose={() => void navigate({ search: (previous) => ({ ...previous, create: undefined }), replace: true })}
        onCreated={(workPackage) => void navigate({
          to: "/contract-work-packages/$packageId",
          params: { packageId: workPackage.id },
        })}
      />
    </Box>
  );
}
